import { useMemo } from 'react';
import ErrorBoundary from '@v2/shared/components/ErrorBoundary';
import ApplePay from '@v2/shared/components/Payment/ApplePay';
import GooglePay from '@v2/shared/components/Payment/GooglePay';
import useWalletReveal from '@v2/shared/components/Payment/useWalletReveal';
import { SubmitPayload, PaymentCart } from '@v2/shared/components/Payment/types';
import { ExpandedProduct, PaymentConfig, Sku } from '@v2/shared/lib/types';

type Props = {
  payment: PaymentConfig | null;
  product: ExpandedProduct;
  sku: Sku;
  submitPayload: SubmitPayload;
  /**
   * That this is the floating bar's copy of the row rather than the buy box's, which
   * shortens Apple Pay's label to fit the column — see `compact` on `ApplePay`. The page
   * renders this component twice and the two placements have very different room.
   */
  compact?: boolean;
};

/**
 * Apple Pay and Google Pay for the selected format, as an express checkout beside Add
 * to Cart.
 *
 * Both are shared components; this is the page's adapter to them — it turns "the format
 * currently selected" into the one-line order the payment sheets price. Quantity is 1
 * and there is no quantity control on this page, which is why it is written here rather
 * than passed in.
 *
 * Each button decides for itself whether to render: no Braintree configuration, no
 * device support, or no card in the wallet, and it draws nothing. So the row here can
 * end up with two buttons, one, or none, and its grid collapses accordingly.
 *
 * Apple Pay comes first, and that order is load-bearing rather than alphabetical: the
 * sticky mobile bar shows only its first two children, so on a device that offers both
 * wallets the order here is what gives Apple Pay the one slot beside Add to Cart. See
 * .product-view-floating-cta in v2.css.
 *
 * That ranking only holds if Apple Pay cannot be beaten to the slot, which is what the
 * ranked reveal below is for: Google Pay is held back until Apple Pay has reported that
 * it has finished deciding, so the bar cannot show the lower-ranked wallet and then swap
 * it for the higher one. Apple Pay itself waits for nothing. See `useWalletReveal`.
 *
 * Each wallet sits in its own silent error boundary. They are optional extras beside Add
 * to Cart, so a wallet that crashes draws nothing instead of taking the product page with
 * it, and a crashed Apple Pay opens Google Pay's gate as if it had decided "no".
 */
export default function WalletButtons({ payment, product, sku, submitPayload, compact = false }: Props) {
  const cart: PaymentCart = useMemo(
    () => ({
      items: [
        {
          product: { id: product.id },
          sku: { code: sku.code },
          quantity: 1,
          price: sku.price,
          // The sheets take a list price and a discount rather than a net price, so
          // that the visitor sees the saving on the Discount line.
          discount: sku.price - (sku.sale_price || sku.price),
        },
      ],
      total_items_price: sku.price,
      total_discounts: sku.price - (sku.sale_price || sku.price),
      // Both start at zero and are filled in by the sheet once an address is given.
      shipping_total_amount: 0,
      tax_total_amount: 0,
    }),
    [product.id, sku]
  );

  // Apple Pay shows the moment it is ready; Google Pay waits until Apple Pay has
  // finished deciding, so it can never take the sticky bar's slot and be swapped out of
  // it a moment later.
  const reveal = useWalletReveal();

  if (!payment) {
    return null;
  }

  return (
    <>
      <ErrorBoundary name="Payment.ApplePay" fallback={null} onError={reveal.primary.reportSettled}>
        <ApplePay
          payment={payment}
          cart={cart}
          submitPayload={submitPayload}
          reveal={reveal.primary}
          compact={compact}
        />
      </ErrorBoundary>
      {payment.googleMerchantId && (
        <ErrorBoundary name="Payment.GooglePay" fallback={null}>
          <GooglePay payment={payment} cart={cart} submitPayload={submitPayload} reveal={reveal.secondary} />
        </ErrorBoundary>
      )}
    </>
  );
}
